/**
 * React Query hooks for PIM Approval API (COMP-005 V7).
 * Approval request management and approval policy administration.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type { ApprovalRequest, ApprovalPolicy } from "@/types";

// ── Approval Request Hooks ──────────────────────────────────────────

export function usePendingApprovals() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["pim", "approvals", "pending"],
    queryFn: () =>
      api.get<{ items: ApprovalRequest[] }>("/api/sessions/pending"),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useApproveRequest() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) =>
      api.post<{ request: ApprovalRequest }>(
        `/api/sessions/${requestId}/approve`,
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pim"] });
    },
  });
}

export function useDenyRequest() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      api.post<{ request: ApprovalRequest }>(
        `/api/sessions/${requestId}/deny`,
        { reason },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pim"] });
    },
  });
}

// ── Approval Policy Hooks (Admin) ───────────────────────────────────

export function useApprovalPolicies() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["pim", "approvals", "policies"],
    queryFn: () =>
      api.get<{ items: ApprovalPolicy[] }>("/api/admin/approval-policies"),
    staleTime: 60_000,
  });
}

export function useUpdateApprovalPolicy() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tier,
      ...body
    }: {
      tier: string;
      approvalMode: string;
      maxDurationHours: number;
      approverGroupId?: string;
      approverGroupName?: string;
      requireJustification: boolean;
    }) =>
      api.put<{ policy: ApprovalPolicy }>(
        `/api/admin/approval-policies/${tier}`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["pim", "approvals", "policies"],
      });
    },
  });
}
